import { Fragment, type ReactNode } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  type AyotHeatDimension,
  type AyotHeatMap,
  type AyotTenure,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import { ayotTrackLabel } from './analytics.labels';
import { mutedTextSx } from './analytics.styles';
import { type InfoNote } from './AnalyticsCard';

const { heatMap } = appStrings.modules.coachingAnalytics.onTrack;
const { academic } = heatMap.info;

const UPPER_TIER = 'upper-tier';

/** A line-break opportunity after each slash, so "Trades/Technical" wraps there rather than mid-word. */
export const breakAfterSlashes = (text: string): ReactNode =>
  text.split('/').map((part, index) => (
    <Fragment key={index}>
      {index > 0 && (
        <>
          /<wbr />
        </>
      )}
      {part}
    </Fragment>
  ));

type Row = Readonly<{ label: string; cells: readonly string[] }>;

const Section = ({ heading, children }: Readonly<{ heading: string; children: ReactNode }>) => (
  <Box sx={sectionSx}>
    <Typography variant="text-md-semibold">{heading}</Typography>
    {children}
  </Box>
);

const Paragraph = ({ text }: Readonly<{ text: string }>) => (
  <Typography variant="text-md-regular" sx={mutedTextSx}>
    {text}
  </Typography>
);

const Measure = ({ name, text }: Readonly<{ name: string; text: string }>) => (
  <Typography variant="text-md-regular" sx={mutedTextSx}>
    <Typography component="span" variant="text-md-semibold" sx={measureNameSx}>
      {name}.
    </Typography>{' '}
    {text}
  </Typography>
);

const MiniTable = ({
  corner,
  columns,
  rows,
}: Readonly<{ corner: string; columns: readonly string[]; rows: readonly Row[] }>) => (
  <Box sx={tableSx(columns.length + 1)}>
    <Typography variant="text-xs-semibold" sx={tableHeadSx}>
      {corner}
    </Typography>
    {columns.map((column) => (
      <Typography key={column} variant="text-xs-semibold" sx={tableHeadSx}>
        {breakAfterSlashes(column)}
      </Typography>
    ))}
    {rows.map((row) => (
      <Fragment key={row.label}>
        <Typography variant="text-xs-semibold" sx={tableRowHeadSx}>
          {row.label}
        </Typography>
        {row.cells.map((cell, index) => (
          <Typography key={index} variant="text-xs-regular" sx={tableCellSx}>
            {cell}
          </Typography>
        ))}
      </Fragment>
    ))}
  </Box>
);

const gpaText = (gpa: number | undefined): string =>
  gpa === undefined ? academic.bandSection.notReachable : gpa.toFixed(1);

/** Minimum GPA per rubric track for each coursework rigor band that has a bar. */
const highSchoolRows = (map: AyotHeatMap, rigor: AyotHeatDimension): Row[] =>
  rigor.buckets
    .map((band) => ({
      label: rigor.bucket_names?.[band] ?? band,
      cells: map.tracks.map((track) =>
        gpaText(
          map.gpa_requirements.high_school.find((req) => req.rigor === band && req.track === track)
            ?.minimum_gpa,
        ),
      ),
    }))
    .filter((row) => row.cells.some((cell) => cell !== academic.bandSection.notReachable));

/** Minimum GPA for Upper-Tier by selectivity (rows) and major rigor (columns). */
const postSecondaryRows = (
  map: AyotHeatMap,
  selectivity: AyotHeatDimension,
  majorRigor: AyotHeatDimension,
): Row[] =>
  selectivity.buckets
    .map((band) => ({
      label: selectivity.bucket_names?.[band] ?? band,
      cells: majorRigor.buckets.map((rigor) =>
        gpaText(
          map.gpa_requirements.post_secondary.find(
            (req) =>
              req.selectivity === band && req.major_rigor === rigor && req.track === UPPER_TIER,
          )?.minimum_gpa,
        ),
      ),
    }))
    .filter((row) => row.cells.some((cell) => cell !== academic.bandSection.notReachable));

/** Info modal notes for the Academic Foundations category, sectioned by how its rows are scored. */
export const academicInfo = (
  map: AyotHeatMap,
  dimensions: readonly AyotHeatDimension[],
  tenure: AyotTenure,
  excluded: readonly InfoNote[],
): InfoNote[] => {
  const byId = (id: string) => dimensions.find((dimension) => dimension.id === id);
  const gpa = byId('gpa');
  const test = byId('test');
  const levels = dimensions.filter((dimension) => dimension.kind === 'level');
  const rigor = byId('rigor');
  const majorRigor = byId('major_rigor');
  const selectivity = byId('selectivity');
  const testStandards = Object.entries(test?.standards[tenure] ?? {});
  const gpaName =
    tenure === 'post_secondary' ? heatMap.measures.gpaPostSecondary : heatMap.measures.gpa;
  const trackNames = map.tracks.map(ayotTrackLabel);
  const excludedNotes = excluded.filter((note): note is string => typeof note === 'string');
  const sectionHeading = (heading: string, names: readonly string[]) =>
    `${heading} — ${names.join(', ')}`;
  const trackNamesShown = [...(gpa ? [gpaName] : []), ...(test ? [heatMap.measures.test] : [])];
  const bandNamesShown = [
    ...(rigor ? [heatMap.measures.rigor] : []),
    ...(majorRigor ? [heatMap.measures.major_rigor] : []),
    ...(selectivity ? [heatMap.measures.selectivity] : []),
  ];

  return [
    academic.intro,
    (gpa || test) && (
      <Section heading={sectionHeading(academic.trackSection.heading, trackNamesShown)}>
        <Paragraph text={academic.trackSection.intro} />
        {gpa && <Measure name={gpaName} text={academic.trackSection.gpa[tenure]} />}
        {test && <Measure name={heatMap.measures.test} text={academic.trackSection.test[tenure]} />}
        {testStandards.length > 0 && (
          <MiniTable
            corner={academic.trackSection.tableRowHeader}
            columns={[heatMap.measures.test]}
            rows={testStandards.map(([track, text]) => ({
              label: ayotTrackLabel(track),
              cells: [text],
            }))}
          />
        )}
      </Section>
    ),
    (rigor || majorRigor || selectivity) && (
      <Section heading={sectionHeading(academic.bandSection.heading, bandNamesShown)}>
        <Paragraph text={academic.bandSection.intro} />
        {rigor && (
          <>
            <Measure name={heatMap.measures.rigor} text={academic.bandSection.rigor} />
            <MiniTable
              corner={academic.bandSection.tableRowHeader.rigor}
              columns={trackNames}
              rows={highSchoolRows(map, rigor)}
            />
          </>
        )}
        {majorRigor && (
          <Measure name={heatMap.measures.major_rigor} text={academic.bandSection.major_rigor} />
        )}
        {selectivity && (
          <Measure name={heatMap.measures.selectivity} text={academic.bandSection.selectivity} />
        )}
        {selectivity && majorRigor && (
          <MiniTable
            corner={academic.bandSection.tableRowHeader.selectivity}
            columns={majorRigor.buckets.map((band) => majorRigor.bucket_names?.[band] ?? band)}
            rows={postSecondaryRows(map, selectivity, majorRigor)}
          />
        )}
      </Section>
    ),
    levels.length > 0 && (
      <Section
        heading={sectionHeading(
          academic.levelSection.heading,
          levels.map((dimension) => dimension.name),
        )}
      >
        <Paragraph text={academic.levelSection.body} />
      </Section>
    ),
    excludedNotes.length > 0 && (
      <Section heading={academic.excludedHeading}>
        {excludedNotes.map((note) => (
          <Paragraph key={note} text={note} />
        ))}
      </Section>
    ),
  ];
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  paddingTop: backrs.spacing['spacing-md'],
});

const measureNameSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const tableSx =
  (columns: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1.4fr) repeat(${columns - 1}, minmax(0, 1fr))`,
    border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    borderRadius: backrs.radius['radius-sm'],
    overflow: 'hidden',
    '& > *': {
      padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-sm']}`,
      borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    },
    [`& > *:nth-last-of-type(-n + ${columns})`]: { borderBottom: 'none' },
  });

const tableHeadSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  hyphens: 'auto',
  overflowWrap: 'anywhere',
});

const tableRowHeadSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const tableCellSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
